import React, { useEffect, useRef } from "react"

import { OBJECT_VIEW_MODE } from "@/lib/viewModes"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../event-bus"
import type { EditableTestCase } from "../../model"
import { CollapsedDataCell } from "./cells/CollapsedDataCell"
import { TestCaseBehaviorsCell } from "./cells/TestCaseBehaviorsCell"
import { TestCaseDescriptionCell } from "./cells/TestCaseDescriptionCell"
import { TestCaseInputCell } from "./cells/TestCaseInputCell"
import { TestCaseKbEntriesCell } from "./cells/TestCaseKbEntriesCell"
import { TestCaseMetadataCell } from "./cells/TestCaseMetadataCell"
import {
  SELECT_CELL_ROLE,
  TestCaseNumberCell,
} from "./cells/TestCaseNumberCell"
import { TestCaseTypeCell } from "./cells/TestCaseTypeCell"
import { MetadataColumnCells } from "./MetadataColumnCells"
import type { ColumnWidths } from "./useResizableColumns"

const COLLAPSED_DATA_WIDTH = "480px"

interface PendingAdditionRowProps {
  testCase: EditableTestCase
  /** Position within the selectable list, which runs the visible rows and then the staged ones; drives range selection. */
  index: number
  /** Position within the full list; drives the displayed row number. */
  displayIndex: number
  /** Whether this is the first staged row, which the editor scrolls into view. */
  isFirst: boolean
  isMultiSelected: boolean
  columnWidths: ColumnWidths
}

export const PendingAdditionRow: React.FC<PendingAdditionRowProps> = ({
  testCase,
  index,
  displayIndex,
  isFirst,
  isMultiSelected,
  columnWidths,
}) => {
  const { visibleColumns, metadataViewMode } = useTestSetEditorContext()
  const dispatch = useEventDispatcher()
  const rowRef = useRef<HTMLTableRowElement>(null)

  // Auto-scroll to first pending addition
  useEffect(() => {
    if (isFirst && rowRef.current) {
      const id = setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 100)
      return () => clearTimeout(id)
    }
  }, [isFirst])

  // No-op for pending additions — edits go through copilot
  const handleUpdate = () => {}

  const handleToggleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    dispatch(EVENTS.TEST_CASE_TOGGLE_SELECT, {
      testCaseId: testCase.id,
      index,
      shiftKey: e.shiftKey,
    })
  }

  return (
    <tr
      ref={rowRef}
      className={`group border-b border-coval-border transition-colors ${
        isMultiSelected ? "bg-surface-2" : "bg-surface-default"
      }`}
      data-test-case-id={testCase.id}
      data-covi-highlight-id={testCase.id}
      data-testid={`test-set-editor-row-${testCase.id}`}
      onKeyDownCapture={(e) => {
        // Every cell here but the selection cell is read-only, so Enter would open an editor whose edits go nowhere.
        if (
          e.key === "Enter" &&
          !(e.target as HTMLElement).closest(
            `[data-cell-role="${SELECT_CELL_ROLE}"]`,
          )
        ) {
          e.stopPropagation()
          e.preventDefault()
        }
      }}
    >
      <TestCaseNumberCell
        index={index}
        displayIndex={displayIndex}
        isMarkedForDeletion={false}
        isMultiSelected={isMultiSelected}
        onToggleSelect={handleToggleSelect}
        width={columnWidths.number}
      />

      {visibleColumns.has("type") && (
        <TestCaseTypeCell
          testCase={testCase}
          onUpdate={handleUpdate}
          width={columnWidths.type}
        />
      )}

      <TestCaseInputCell
        testCase={testCase}
        pendingEdit={null}
        isExpanded
        isMarkedForDeletion={false}
        onUpdate={handleUpdate}
        width={columnWidths.description}
      />

      {visibleColumns.has("description") && (
        <TestCaseDescriptionCell
          testCase={testCase}
          isExpanded
          onUpdate={handleUpdate}
          width={columnWidths.testCaseDescription}
        />
      )}

      {metadataViewMode === OBJECT_VIEW_MODE.JSON &&
      (visibleColumns.has("behaviors") ||
        visibleColumns.has("metadata") ||
        visibleColumns.has("kbEntries")) ? (
        <CollapsedDataCell
          testCase={testCase}
          isExpanded
          width={COLLAPSED_DATA_WIDTH}
        />
      ) : null}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("behaviors") && (
          <TestCaseBehaviorsCell
            testCase={testCase}
            isExpanded
            width={columnWidths.behaviors}
          />
        )}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("metadata") &&
        (metadataViewMode === OBJECT_VIEW_MODE.COLUMNS ? (
          <MetadataColumnCells testCase={testCase} isExpanded />
        ) : (
          <TestCaseMetadataCell
            testCase={testCase}
            isExpanded
            width={columnWidths.metadata}
          />
        ))}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("kbEntries") && (
          <TestCaseKbEntriesCell
            testCase={testCase}
            isExpanded
            width={columnWidths.kbEntries}
          />
        )}

      {/* Empty cell for "+ Add Column" header */}
      {columnWidths.addColumn !== "0px" && (
        <td className="p-0" style={{ width: columnWidths.addColumn }} />
      )}
    </tr>
  )
}
